import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, DemoBadge } from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Briefcase, Users, ShieldCheck, Building2, ArrowRight } from "lucide-react";

const ADVISOR_IMG = "https://images.unsplash.com/photo-1765648684555-de2d0f6af467?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxzbWlsaW5nJTIwcHJvZmVzc2lvbmFsJTIwZGVza3xlbnwwfHx8fDE3ODgxNDUwMjV8MA&ixlib=rb-4.1.0&q=85";

export default function AdvisorPortal() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);

  useEffect(() => {
    api.get("/advisor/overview").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const openClient = async (client) => {
    setSelected(client);
    setClientDetail(null);
    const { data } = await api.get(`/advisor/clients/${client.id}/employees`);
    setClientDetail(data);
  };

  if (loading) return <div className="p-6 sm:p-12 max-w-6xl mx-auto"><Skeleton className="h-96 w-full rounded-xl" /></div>;

  const totals = data?.totals || {};
  const stats = [
    { label: "Client companies", value: totals.client_count || 0, icon: Building2 },
    { label: "Covered employees", value: totals.employee_count || 0, icon: Users },
    { label: "Enrolled", value: totals.enrolled_count || 0, icon: ShieldCheck },
  ];

  return (
    <div className="p-6 sm:p-10 lg:p-12 max-w-6xl mx-auto animate-rise">
      <div className="relative rounded-2xl overflow-hidden mb-8 bg-navy-800">
        <img src={ADVISOR_IMG} alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-20" />
        <div className="relative p-8">
          <div className="flex items-center gap-2 mb-1">
            <Briefcase className="w-4 h-4 text-gold" />
            <span className="text-gold text-xs tracking-[0.2em] uppercase font-bold">Advisor command center</span>
            <DemoBadge />
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-white tracking-tight">Your client book</h1>
          <p className="text-mist-200 mt-2 max-w-xl">Assist your employer clients and their employees through enrollment — all from one view.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-xl border border-mist-200 p-6" data-testid={`advisor-stat-${s.label.split(" ")[0].toLowerCase()}`}>
              <div className="w-11 h-11 rounded-lg bg-mist-100 flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-navy-800" /></div>
              <div className="font-display text-4xl font-bold text-navy-900">{s.value}</div>
              <p className="text-sm text-muted-foreground mt-1">{s.label}</p>
            </div>
          );
        })}
      </div>

      <h3 className="font-display text-xl font-bold text-navy-900 mb-4">Client companies</h3>
      <div className="grid md:grid-cols-2 gap-5">
        {data?.clients?.map((c) => (
          <button key={c.id} onClick={() => openClient(c)} className="text-left bg-white rounded-xl border border-mist-200 p-6 card-lift" data-testid={`advisor-client-${c.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-display text-xl font-bold text-navy-900">{c.name}</h4>
                <p className="text-sm text-muted-foreground">{c.industry} · {c.state} · Group {c.group_number}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-mist-300" />
            </div>
            <div className="mt-5">
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-muted-foreground">{c.enrolled_count}/{c.employee_count} enrolled</span>
                <span className="font-bold text-gold-dark">{c.completion_pct}%</span>
              </div>
              <Progress value={c.completion_pct} className="h-2" />
            </div>
          </button>
        ))}
        {!data?.clients?.length && (
          <div className="col-span-2 bg-white rounded-xl border border-dashed border-mist-300 p-12 text-center text-muted-foreground">No clients assigned yet.</div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="advisor-client-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{selected?.name}</DialogTitle></DialogHeader>
          {!clientDetail ? <Skeleton className="h-40 w-full" /> : (
            <div className="space-y-2">
              {clientDetail.employees.length ? clientDetail.employees.map((e) => (
                <div key={e.id} className="flex items-center justify-between p-3 rounded-lg border border-mist-200">
                  <div>
                    <p className="font-semibold text-navy-900 text-sm">{e.name}</p>
                    <p className="text-xs text-muted-foreground">{e.email}</p>
                  </div>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-mist-100 text-navy-700">{e.active_benefits} benefits</span>
                </div>
              )) : <p className="text-sm text-muted-foreground text-center py-6">No employees enrolled yet.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
